import axios from "axios";
import { getDeviceId } from "../utils/deviceId";
import {
  startTokenRefresher,
  refreshSession,
  isSessionRejected,
  endSession,
} from "../utils/RefreshToken";
import { isImpersonating, endImpersonationLocally } from "../utils/impersonation";
import { getApiBaseUrl, getTenantId } from "./tenantApi";


const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});


axiosInstance.interceptors.request.use(async (config) => {

  const token = localStorage.getItem("token");
  
  const deviceId = getDeviceId();
  config.headers["X-Device-Type"] = "web";
  config.headers["X-Device-Id"] = deviceId;
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Multi-tenant: talk to the tenant's own API host and identify the tenant on
  // every request. Upstream is single-tenant and has neither.
  const baseUrl = getApiBaseUrl();
  if (baseUrl) {
    config.baseURL = baseUrl;
  }
  if (typeof window !== "undefined" && window.location?.hostname) {
    config.headers["X-Tenant-Domain"] = window.location.hostname;
  }
  const tid = getTenantId();
  if (tid) config.headers["X-Tenant-Id"] = tid;

  return config;
});

//  Response Interceptor – handles 401s
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // ── RBAC permission denials ───────────────────────────────────────────
    // Back-office staff routinely trigger background data fetches for modules
    // they can't access (e.g. the Add Content form loading Live TV options). The
    // server correctly returns 403 "…does not allow…". For READ requests we
    // swallow that quietly and resolve an empty payload so the page renders
    // without that optional data — no scary toast. WRITE requests are NOT
    // swallowed: a blocked save must still surface so the user knows it failed.
    const status = error.response?.status;
    const serverMsg = error.response?.data?.message || "";
    const method = (originalRequest?.method || "get").toLowerCase();
    if (
      status === 403 &&
      /does not allow/i.test(serverMsg) &&
      method === "get"
    ) {
      return Promise.resolve({
        data: { status: "error", message: serverMsg, data: [], permissionDenied: true },
        status: 403,
        statusText: "Forbidden",
        headers: error.response?.headers || {},
        config: originalRequest,
        permissionDenied: true,
      });
    }

    // Creator-portal session ended or expired (it can't be refreshed): go
    // back to the admin's own session.
    if (status === 401 && isImpersonating()) {
      endImpersonationLocally();
      return Promise.reject(error);
    }

    //  DO NOT refresh token for login API
    if (originalRequest?.url?.includes("/login")) {
      return Promise.reject(error);
    }

    // Only attempt refresh if refresh token exists
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      return Promise.reject(error);
    }

    // Handle 401 normally
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      // Concurrent 401s (and the interval refresher) all await the same
      // in-flight refresh, then each retries with the new token.
      let newToken;
      try {
        ({ token: newToken } = await refreshSession());
      } catch (err) {
        console.error("Refresh token failed", err);
        // Only log out when the server rejected the refresh token; a network
        // blip or 5xx just fails this request and the session stays intact.
        if (isSessionRejected(err)) endSession();
        return Promise.reject(err);
      }
      // Kept outside the try so a failing retry isn't mistaken for a failed refresh.
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return axiosInstance(originalRequest);
    }

    return Promise.reject(error);
  }
);


//Start the token refresher on load
// Start refresher ONLY if refresh token exists
const refreshToken = localStorage.getItem("refreshToken");
if (refreshToken) {
  startTokenRefresher();
}


export default axiosInstance;
